import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Snowflake } from 'lucide-react';
import SaidaDeEmbrioes from './SaidaDeEmbrioes';
import { MOTIVOS_DE_SAIDA } from '../../../data/estoque';
import {
  listarDescongelados, ordenaPorPrazo, limiteDoDescongelado, hojeLocal,
} from '../../../data/descongelados';
import { MARK } from '../../../constants';

const SO_PERDA = MOTIVOS_DE_SAIDA.filter(m => m.id === 'perda');

const dataBr = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR');

/**
 * O que saiu do botijão e não foi transferido.
 *
 * Não é mais estoque, e também não é perda até alguém confirmar: fora desta
 * lista, a sobra sumiria do botijão sem ninguém ver. Só aparece quando há o que
 * mostrar.
 *
 * A seleção é por embrião, ao contrário do botijão: a haste já foi aberta.
 * A baixa só oferece perda, porque descongelado ou é usado ou é perda.
 */
const SobrasDescongeladas = ({ laboratoryId, podeMexer = true }) => {
  const [linhas, setLinhas] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [erro, setErro] = useState('');
  const [dandoBaixa, setDandoBaixa] = useState(false);

  const carregar = useCallback(async () => {
    if (!laboratoryId) return;
    try {
      setLinhas(await listarDescongelados(laboratoryId) || []);
      setSel(new Set());
      setErro('');
    } catch (e) {
      console.error('sobras descongeladas:', e);
      setErro(e.message);
    }
  }, [laboratoryId]);

  useEffect(() => { carregar(); }, [carregar]);

  const hoje = hojeLocal();
  const ordenadas = useMemo(() => ordenaPorPrazo(linhas, hoje), [linhas, hoje]);

  const alternar = (id) => setSel(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  if (erro) {
    return (
      <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2">
        {erro}
      </div>
    );
  }
  if (ordenadas.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
        <Snowflake size={14} className="text-slate-400" />
        <span className="font-medium text-slate-900 text-sm">Descongelados sem uso</span>
        <span className="text-xs text-slate-500">
          Fora do botijão. Use em outra TE em até 48 horas; depois disso, é perda.
        </span>
        {podeMexer && sel.size > 0 && (
          <button onClick={() => setDandoBaixa(true)}
            className="ml-auto px-3 py-1.5 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
            Dar baixa como perda ({sel.size})
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 border-b border-slate-100">
            <tr>
              {podeMexer && <th className="w-8"></th>}
              <th className="px-2 py-1.5 text-left font-medium">Prazo</th>
              <th className="px-2 py-1.5 text-left font-medium">Haste</th>
              <th className="px-2 py-1.5 text-left font-medium">Cliente</th>
              <th className="px-2 py-1.5 text-left font-medium">Doadora × Touro</th>
              <th className="px-2 py-1.5 text-center font-medium">Fase</th>
              <th className="px-2 py-1.5 text-left font-medium">Descongelado em</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {ordenadas.map(l => {
              const limite = limiteDoDescongelado(l.descongelado_em);
              return (
                <tr key={l.embryo_id} className={sel.has(l.embryo_id) ? 'bg-blue-50' : 'hover:bg-slate-50'}>
                  {podeMexer && (
                    <td className="pl-4">
                      <input type="checkbox" checked={sel.has(l.embryo_id)}
                        onChange={() => alternar(l.embryo_id)}
                        className="rounded border-slate-300" />
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                    {limite < hoje
                      ? `${MARK.WARN} venceu em ${dataBr(limite)}`
                      : `${MARK.PENDING} até ${dataBr(limite)}`}
                  </td>
                  <td className="px-2 py-1.5 text-slate-700">{l.haste ? `H${l.haste}` : '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700">{l.client_name || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-700">
                    {l.doadora || '—'}<span className="text-slate-400"> × {l.touro || '—'}</span>
                  </td>
                  <td className="px-2 py-1.5 text-center text-slate-600">{l.stage || '—'}</td>
                  <td className="px-2 py-1.5 text-slate-500 text-xs whitespace-nowrap">
                    {dataBr(l.descongelado_em)}
                    {l.te_numero ? ` · TE do serviço #${l.te_numero}` : ''}
                    {l.te_cliente ? ` · ${l.te_cliente}` : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {dandoBaixa && (
        <SaidaDeEmbrioes
          selecionados={ordenadas.filter(l => sel.has(l.embryo_id))}
          motivos={SO_PERDA}
          rotuloDaLista="Dar baixa em"
          onPronto={carregar}
          onFechar={() => setDandoBaixa(false)}
        />
      )}
    </div>
  );
};

export default SobrasDescongeladas;
